import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Trash2, Users, AlertTriangle } from "lucide-react";
import { QuestionType, SegmentationRule, LogicCondition, getSelectableOptions } from "@/types/survey";

// Question types that have selectable options (answers stored as option IDs)
const CHOICE_QUESTION_TYPES = [
  "single_choice",
  "multiple_choice", 
  "dropdown",
  "yes_no",
  "likert_scale",
];

// Only radio_grid and checkbox_grid support row_equals (column selection semantics)
// We intentionally exclude dropdown_matrix, text_matrix, numeric_matrix, ranking_matrix
const ROW_EQUALS_SUPPORTED_TYPES = ["radio_grid", "checkbox_grid"];

// Helper: Check if a question type supports row_equals operator
const supportsRowEquals = (q: QuestionType): boolean => {
  return ROW_EQUALS_SUPPORTED_TYPES.includes(q.type);
};

// Helper to format matrix value for display (ID-based only)
// Helper to format grid value for display (ID-based only)
// Note: Only applies to radio_grid and checkbox_grid question types
const formatGridValue = (
  value: unknown,
  question: QuestionType | undefined
): string => {
  if (!question || typeof value !== "object" || value === null || Array.isArray(value)) {
    return String(value || "");
  }
  
  const gridValue = value as { rowId: string; columnId: string };
  const rows = question.rows || [];
  const columns = question.columns || [];
  
  // ID-based lookup only
  const rowLabel = rows.find(r => r.id === gridValue.rowId)?.label || "?";
  const colLabel = columns.find(c => c.id === gridValue.columnId)?.label || "?";
  
  return `${rowLabel} = ${colLabel}`;
};

interface SegmentationRuleEditorProps {
  question: QuestionType;
  allQuestions: QuestionType[];
  onChange: (rules: SegmentationRule[]) => void;
}

export function SegmentationRuleEditor({
  question,
  allQuestions,
  onChange,
}: SegmentationRuleEditorProps) {
  const rules = question.segmentationRules || [];
  
  // Get questions available for conditions (including current question for self-reference)
  // Segmentation rules are evaluated AFTER the question is answered, so self-reference is valid
  const currentIndex = allQuestions.findIndex(q => q.id === question.id);
  const availableQuestions = allQuestions.slice(0, currentIndex + 1);

  // Helper: Check if a question type has selectable options
  // Include questions with allowOther even if no explicit options
  const isChoiceQuestion = (q: QuestionType): boolean => {
    const hasOptions = !!q.options && q.options.length > 0;
    const hasOther = !!q.allowOther;
    return CHOICE_QUESTION_TYPES.includes(q.type) && (hasOptions || hasOther);
  };

  // Helper: Get question by ID
  const getQuestionById = (questionId: string): QuestionType | undefined => {
    return allQuestions.find(q => q.id === questionId);
  };

  // Helper: Detect suboptimal rule placement (Phase 6.4)
  // Warns when a rule only references an earlier question (could be placed there instead)
  const getPlacementWarning = (rule: SegmentationRule): string | null => {
    if (!rule.conditions || rule.conditions.length === 0) {
      return null;
    }
    
    // Get unique question IDs referenced in conditions
    const referencedQuestionIds = new Set(
      rule.conditions.map(c => c.questionId).filter(Boolean)
    );
    
    // If only one question is referenced
    if (referencedQuestionIds.size === 1) {
      const referencedId = Array.from(referencedQuestionIds)[0];
      const referencedIndex = allQuestions.findIndex(q => q.id === referencedId);
      
      // If that question comes BEFORE the current question
      if (referencedIndex >= 0 && referencedIndex < currentIndex) {
        const referencedQ = allQuestions[referencedIndex];
        const qNum = referencedQ.originalQuestionNumber || `Q${referencedIndex + 1}`;
        return `This rule only references ${qNum}. For earlier screening, add it directly to ${qNum}.`;
      }
    }
    
    return null;
  };

  const addRule = () => {
    onChange([
      ...rules,
      {
        segmentName: "",
        conditions: [],
        conditionOperator: "AND",
      },
    ]);
  };

  const updateRule = (index: number, updates: Partial<SegmentationRule>) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], ...updates };
    onChange(newRules);
  };

  const removeRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const addCondition = (ruleIndex: number) => {
    const newRules = [...rules];
    newRules[ruleIndex].conditions.push({
      questionId: availableQuestions[0]?.id || "",
      operator: "equals",
      value: "",
    });
    onChange(newRules);
  };

  const updateCondition = (
    ruleIndex: number,
    condIndex: number,
    updates: Partial<LogicCondition>
  ) => {
    const newRules = [...rules];
    newRules[ruleIndex].conditions[condIndex] = {
      ...newRules[ruleIndex].conditions[condIndex],
      ...updates,
    };
    onChange(newRules);
  };

  const removeCondition = (ruleIndex: number, condIndex: number) => {
    const newRules = [...rules];
    newRules[ruleIndex].conditions = newRules[ruleIndex].conditions.filter(
      (_, i) => i !== condIndex
    );
    onChange(newRules);
  };

  // Helper to truncate text
  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Render conditions for a rule
  const renderConditions = (rule: SegmentationRule, ruleIndex: number) => {
    return (
      <div className="space-y-2">
        {rule.conditions.map((cond, condIndex) => (
          <div key={condIndex} className="space-y-1">
            <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/50 rounded-lg">
              {/* Question Selector */}
              <Select
                value={cond.questionId}
                onValueChange={(v) => updateCondition(ruleIndex, condIndex, { 
                  questionId: v,
                  value: ""
                })}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select question" />
                </SelectTrigger>
                <SelectContent>
                  {availableQuestions.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.originalQuestionNumber || "Q?"}: {truncate(q.question, 30)}
                      {q.id === question.id && " (this)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Operator Selector */}
              <Select
                value={cond.operator}
                onValueChange={(v) => updateCondition(ruleIndex, condIndex, { 
                  operator: v as LogicCondition["operator"],
                  value: (v === "row_equals" || v === "row_not_equals") 
                    ? { rowId: "", columnId: "" } 
                    : ""
                })}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const selectedQuestion = getQuestionById(cond.questionId);
                    const isMatrixQuestion = selectedQuestion && supportsRowEquals(selectedQuestion);
                    
                    if (isMatrixQuestion) {
                      return (
                        <>
                          <SelectItem value="row_equals">row equals</SelectItem>
                          <SelectItem value="row_not_equals">row not equals</SelectItem>
                        </>
                      );
                    } else {
                      return (
                        <>
                          <SelectItem value="equals">equals</SelectItem>
                          <SelectItem value="not_equals">not equals</SelectItem>
                          <SelectItem value="contains">contains</SelectItem>
                          <SelectItem value="in">is one of</SelectItem>
                        </>
                      );
                    }
                  })()}
                </SelectContent>
              </Select>
              
              {/* Value Input */}
              {(() => {
                const selectedQuestion = getQuestionById(cond.questionId);
                
                // Grid question with row_equals/row_not_equals
                if (selectedQuestion && supportsRowEquals(selectedQuestion) && 
                    (cond.operator === "row_equals" || cond.operator === "row_not_equals")) {
                  const rows = selectedQuestion.rows || [];
                  const columns = selectedQuestion.columns || [];
                  const rawValue = (typeof cond.value === "object" && cond.value !== null && !Array.isArray(cond.value))
                    ? cond.value as { rowId?: string; columnId?: string }
                    : {};
                  const currentRowId = rawValue.rowId || "";
                  const currentColumnId = rawValue.columnId || "";
                  
                  return (
                    <div className="flex gap-2">
                      <Select
                        value={currentRowId}
                        onValueChange={(v) => updateCondition(ruleIndex, condIndex, { 
                          value: { rowId: v, columnId: currentColumnId }
                        })}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue placeholder="Row" />
                        </SelectTrigger>
                        <SelectContent>
                          {rows.map((row) => (
                            <SelectItem key={row.id} value={row.id}>{row.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <span className="text-muted-foreground self-center">=</span>
                      
                      <Select
                        value={currentColumnId}
                        onValueChange={(v) => updateCondition(ruleIndex, condIndex, { 
                          value: { rowId: currentRowId, columnId: v }
                        })}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue placeholder="Column" />
                        </SelectTrigger>
                        <SelectContent>
                          {columns.map((col) => (
                            <SelectItem key={col.id} value={col.id}>{col.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                }
                
                // Fallback for matrix value display
                if (typeof cond.value === "object" && cond.value !== null && 
                    !Array.isArray(cond.value) && "rowId" in cond.value && "columnId" in cond.value) {
                  return (
                    <span className="text-sm text-muted-foreground px-2 py-1 bg-muted rounded">
                      {formatGridValue(cond.value, selectedQuestion)}
                    </span>
                  );
                }
                
                // Choice question: dropdown of options
                if (selectedQuestion && isChoiceQuestion(selectedQuestion)) {
                  const selectableOptions = getSelectableOptions(selectedQuestion);
                  return (
                    <Select
                      value={String(cond.value || "")}
                      onValueChange={(v) => updateCondition(ruleIndex, condIndex, { value: v })}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select answer" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectableOptions.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.text}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                }
                
                // Default: Text input
                return (
                  <Input
                    placeholder="Enter value"
                    value={String(cond.value || "")}
                    onChange={(e) => updateCondition(ruleIndex, condIndex, { value: e.target.value })}
                    className="w-[180px]"
                  />
                );
              })()}
              
              {/* Remove Condition */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeCondition(ruleIndex, condIndex)}
                aria-label="Remove condition"
                className="h-8 w-8 shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Warning: Multi-select operator mismatch */}
            {(() => {
              const selectedQ = getQuestionById(cond.questionId);
              if (selectedQ?.type === "multiple_choice" && 
                  (cond.operator === "equals" || cond.operator === "not_equals")) {
                return (
                  <Alert className="p-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30">
                    <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                    <AlertDescription className="text-xs">
                      Multi-select question: use "contains" for reliable matching.
                    </AlertDescription>
                  </Alert>
                );
              }
              return null;
            })()}
          </div>
        ))}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addCondition(ruleIndex)}
        >
          <Plus className="h-4 w-4 mr-1" /> Add condition
        </Button>
      </div>
    );
  };

  // Edge case: question not found in list
  if (availableQuestions.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        No questions available for segmentation rules.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <Label className="text-sm font-medium">Segmentation Rules</Label>
      </div>
      
      {rules.map((rule, ruleIndex) => (
        <Card key={ruleIndex} className="border-border">
          <CardContent className="p-4 space-y-4">
            {/* Segment Name */}
            <div className="grid gap-2">
              <Label htmlFor={`segment-${ruleIndex}`} className="text-sm">
                Segment Name
              </Label>
              <Input
                id={`segment-${ruleIndex}`}
                value={rule.segmentName}
                onChange={(e) => updateRule(ruleIndex, { segmentName: e.target.value })}
                placeholder="e.g., Male 18-24"
              />
              {/* Warning: Empty conditions match all */}
              {rule.segmentName?.trim() && (!rule.conditions || rule.conditions.length === 0) && (
                <Alert className="mt-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <AlertDescription className="text-sm">
                    No conditions defined — this rule will apply to <strong>all respondents</strong>.
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            {/* Quota Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`min-${ruleIndex}`} className="text-sm">
                  Min Quota
                </Label>
                <Input
                  id={`min-${ruleIndex}`}
                  type="number"
                  min={0}
                  value={rule.quotaMin ?? ""}
                  onChange={(e) => updateRule(ruleIndex, { 
                    quotaMin: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`max-${ruleIndex}`} className="text-sm">
                  Max Quota
                </Label>
                <Input
                  id={`max-${ruleIndex}`}
                  type="number"
                  min={0}
                  value={rule.quotaMax ?? ""}
                  onChange={(e) => updateRule(ruleIndex, { 
                    quotaMax: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                  placeholder="Required"
                />
              </div>
            </div>
            
            {/* Group ID (for per-wave/per-panel quotas) - preserved from existing */}
            <div className="space-y-2">
              <Label className="text-sm">
                Group ID <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                placeholder="e.g., wave_1, panel_A"
                value={rule.groupId ?? ""}
                onChange={(e) => updateRule(ruleIndex, { 
                  groupId: e.target.value || undefined 
                })}
              />
              <p className="text-xs text-muted-foreground">
                Use group ID for per-wave or per-panel quotas
              </p>
            </div>
            
            {/* Conditions */}
            <div className="space-y-2">
              <Label className="text-sm">Conditions</Label>
              {renderConditions(rule, ruleIndex)}
            </div>
            
            {/* AND/OR Toggle - shown when multiple conditions */}
            {rule.conditions.length > 1 && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground">Match</span>
                <Select
                  value={rule.conditionOperator}
                  onValueChange={(v) => updateRule(ruleIndex, { 
                    conditionOperator: v as "AND" | "OR" 
                  })}
                >
                  <SelectTrigger className="w-[100px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">All (AND)</SelectItem>
                    <SelectItem value="OR">Any (OR)</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">conditions</span>
              </div>
            )}
            
            {/* Placement Warning */}
            {getPlacementWarning(rule) && (
              <Alert className="mt-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-sm">
                  {getPlacementWarning(rule)}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
          
          {/* Remove Button */}
          <div className="flex justify-end p-3 pt-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeRule(ruleIndex)}
              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              aria-label={`Remove segment ${rule.segmentName || 'rule'}`}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Remove
            </Button>
          </div>
        </Card>
      ))}
      
      {/* Add Segment Button */}
      <Button variant="outline" onClick={addRule} className="w-full border-dashed">
        <Plus className="h-4 w-4 mr-2" />
        Add Segmentation Rule
      </Button>
    </div>
  );
}
